import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import api from '../../services/api'
import * as XLSX from 'xlsx'

interface ReportCandidate {
  candidate_name: string
  email: string
  college: string
  drive_title: string
  role: string
  tier: string
  final_score: number
  status: string
}

const PAGE_SIZE = 50

const AdminReportPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const collegeTypeParam = searchParams.get('type') as 'engineering' | 'management' | null
  const [collegeType, setCollegeType] = useState<'engineering' | 'management' | null>(collegeTypeParam)

  const [candidates, setCandidates] = useState<ReportCandidate[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  const [filterCollege, setFilterCollege] = useState('')
  const [filterCandidate, setFilterCandidate] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filterDrive, setFilterDrive] = useState('')

  const [debouncedCollege, setDebouncedCollege] = useState('')
  const [debouncedCandidate, setDebouncedCandidate] = useState('')
  const [debouncedRole, setDebouncedRole] = useState('')
  const [debouncedDrive, setDebouncedDrive] = useState('')

  const [shortlistScore, setShortlistScore] = useState<number | ''>('')

  // Debounce all filter inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCollege(filterCollege)
      setDebouncedCandidate(filterCandidate)
      setDebouncedRole(filterRole)
      setDebouncedDrive(filterDrive)
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [filterCollege, filterCandidate, filterRole, filterDrive])

  // Fetch report data
  const fetchReport = useCallback(async () => {
    if (!collegeType) return
    setIsLoading(true)
    try {
      const params: Record<string, string | number> = {
        college_type: collegeType,
        page,
        page_size: PAGE_SIZE,
      }
      if (debouncedCollege) params.search_college = debouncedCollege
      if (debouncedCandidate) params.search_candidate = debouncedCandidate
      if (debouncedRole) params.search_role = debouncedRole
      if (debouncedDrive) params.search_drive = debouncedDrive
      if (shortlistScore !== '' && shortlistScore !== null && shortlistScore > 0) params.min_score = shortlistScore

      const response = await api.get('/admin/report', { params, timeout: 180000 })
      const data = response.data
      setCandidates(data.candidates || [])
      setTotal(data.total || 0)
      setTotalPages(data.total_pages || 0)
    } catch (error) {
      console.error('Error fetching report:', error)
      toast.error('Failed to load report data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [collegeType, page, debouncedCollege, debouncedCandidate, debouncedRole, debouncedDrive, shortlistScore])

  useEffect(() => {
    if (collegeType) {
      fetchReport()
    }
  }, [fetchReport, collegeType])

  const handleSelectCollegeType = (type: 'engineering' | 'management') => {
    setCollegeType(type)
    setSearchParams({ type })
    setPage(1)
    setFilterCollege('')
    setFilterCandidate('')
    setFilterRole('')
    setFilterDrive('')
    setDebouncedCollege('')
    setDebouncedCandidate('')
    setDebouncedRole('')
    setDebouncedDrive('')
    setShortlistScore('')
    setCandidates([])
    setTotal(0)
    setTotalPages(0)
  }

  // Export all data (not just current page) to Excel
  const handleExport = async () => {
    if (!collegeType) return
    try {
      toast.loading('Preparing export...')

      // Fetch ALL data (no pagination) for export
      const params: Record<string, string | number> = {
        college_type: collegeType,
        page: 1,
        page_size: 100000, // large number to get all
      }
      if (debouncedCollege) params.search_college = debouncedCollege
      if (debouncedCandidate) params.search_candidate = debouncedCandidate
      if (debouncedRole) params.search_role = debouncedRole
      if (debouncedDrive) params.search_drive = debouncedDrive
      if (shortlistScore !== '' && shortlistScore !== null && shortlistScore > 0) params.min_score = shortlistScore

      const response = await api.get('/admin/report', { params, timeout: 300000 })
      const allCandidates: ReportCandidate[] = response.data.candidates || []

      toast.dismiss()

      if (allCandidates.length === 0) {
        toast.error('No data to export')
        return
      }

      const isEngineering = collegeType === 'engineering'
      const exportData = allCandidates.map((c, index) => {
        const row: Record<string, unknown> = {
          'S.No': index + 1,
          'Candidate Name': c.candidate_name,
          'Email': c.email,
          'College': c.college,
        }
        // Role column only for Engineering
        if (isEngineering) {
          row['Role'] = c.role
        }
        row['Final Score (%)'] = c.final_score
        row['Status'] = c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1).replace('_', ' ') : ''
        return row
      })

      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Report')

      // Auto-size columns
      const maxWidth = 50
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.min(maxWidth, Math.max(key.length, ...exportData.map((row: Record<string, unknown>) => String(row[key] || '').length)))
      }))
      ws['!cols'] = colWidths

      const label = collegeType === 'engineering' ? 'Engineering' : 'Management'
      const fileName = `${label}_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`
      XLSX.writeFile(wb, fileName)

      toast.success(`Exported ${allCandidates.length} record(s) successfully!`)
    } catch (error) {
      toast.dismiss()
      console.error('Error exporting report:', error)
      toast.error('Failed to export report')
    }
  }

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      completed: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      registered: 'bg-blue-100 text-blue-800',
    }
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ') : 'N/A'
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {label}
      </span>
    )
  }

  // ——— College Type Selection Screen ———
  if (!collegeType) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Report</h1>
            <p className="text-gray-600 mt-2">Select a college type to view candidate report</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Engineering */}
            <button
              onClick={() => handleSelectCollegeType('engineering')}
              className="group bg-white rounded-xl shadow-sm border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all duration-200 p-8 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                  <AcademicCapIcon className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                Engineering Colleges
              </h3>
              <p className="text-gray-500 mt-2 text-sm">
                View all candidates registered in Engineering drives
              </p>
            </button>

            {/* Management */}
            <button
              onClick={() => handleSelectCollegeType('management')}
              className="group bg-white rounded-xl shadow-sm border-2 border-gray-200 hover:border-purple-500 hover:shadow-lg transition-all duration-200 p-8 text-left"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                  <BuildingOfficeIcon className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                Management Colleges
              </h3>
              <p className="text-gray-500 mt-2 text-sm">
                View all candidates registered in Management drives
              </p>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ——— Report Table Screen ———
  const label = collegeType === 'engineering' ? 'Engineering Colleges' : 'Management Colleges'
  const accentColor = collegeType === 'engineering' ? 'blue' : 'purple'

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => {
              setCollegeType(null)
              setSearchParams({})
              setCandidates([])
              setTotal(0)
              setTotalPages(0)
              setPage(1)
            }}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to College Type Selection
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {label} — Candidate Report
              </h1>
              <p className="text-gray-500 mt-1 text-sm">
                {total} total candidate record{total !== 1 ? 's' : ''} found
              </p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${accentColor}-100 text-${accentColor}-800`}>
              {label}
            </span>
          </div>
        </div>

        {/* Filters bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          {/* Row 1: Search filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {/* Search by College */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by College..."
                value={filterCollege}
                onChange={(e) => setFilterCollege(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Search by Candidate */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by Candidate..."
                value={filterCandidate}
                onChange={(e) => setFilterCandidate(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            {/* Search by Role — Engineering only */}
            {collegeType === 'engineering' && (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by Role..."
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            )}

            {/* Search by Drive */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by Drive..."
                value={filterDrive}
                onChange={(e) => setFilterDrive(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Row 2: Shortlist + Export */}
          <div className="flex flex-wrap items-center justify-end gap-4">
            {/* Shortlist */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Shortlist (Min Score %):</label>
              <input
                type="number"
                min="0"
                max="100"
                value={shortlistScore}
                onChange={(e) => {
                  const v = e.target.value === '' ? '' : Number(e.target.value)
                  setShortlistScore(v)
                  setPage(1)
                }}
                className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="0"
              />
            </div>

            {/* Export */}
            <button
              onClick={handleExport}
              className="btn btn-primary flex items-center whitespace-nowrap"
              disabled={isLoading}
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export to Excel
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading report data...</p>
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-16">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 text-lg font-medium">No records found</p>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your search or shortlist filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Candidate Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        College
                      </th>
                      {collegeType === 'engineering' && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Final Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {candidates.map((c, idx) => (
                      <tr key={`${c.email}-${c.drive_title}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{c.candidate_name}</div>
                            <div className="text-xs text-gray-500">{c.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {c.college}
                        </td>
                        {collegeType === 'engineering' && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {c.role}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${c.final_score > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                            {c.final_score}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(c.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span>
                        {' '}to{' '}
                        <span className="font-medium">{Math.min(page * PAGE_SIZE, total)}</span>
                        {' '}of{' '}
                        <span className="font-medium">{total}</span>
                        {' '}results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        {/* Previous */}
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>

                        {/* Page numbers */}
                        {(() => {
                          const pages: number[] = []
                          const maxVisible = 7
                          if (totalPages <= maxVisible) {
                            for (let i = 1; i <= totalPages; i++) pages.push(i)
                          } else {
                            // Always show first, last, and pages around current
                            const start = Math.max(2, page - 1)
                            const end = Math.min(totalPages - 1, page + 1)
                            pages.push(1)
                            if (start > 2) pages.push(-1) // ellipsis
                            for (let i = start; i <= end; i++) pages.push(i)
                            if (end < totalPages - 1) pages.push(-2) // ellipsis
                            pages.push(totalPages)
                          }

                          return pages.map((p, i) => {
                            if (p < 0) {
                              return (
                                <span key={`ellipsis-${i}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                  ...
                                </span>
                              )
                            }
                            return (
                              <button
                                key={p}
                                onClick={() => setPage(p)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  p === page
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {p}
                              </button>
                            )
                          })
                        })()}

                        {/* Next */}
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminReportPage

